import {
    Box,
    Button,
    Container,
    Divider,
    Flex,
    Text,
    useClipboard,
    useToast,
} from '@chakra-ui/react';
import { useGetOrderQuery } from '../../../redux/platimarket/platimarket.api';
import { Link, useParams } from 'react-router-dom';
import Colors from '../../../config/colors';
import { ArrowForwardIcon, CheckIcon, CopyIcon } from '@chakra-ui/icons';
import truncate from '../../../utils/truncate';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ORDER_STATUSES } from '../../../config/orders';
import Loader from '../../loader/Loader';

import { IoGameControllerOutline } from 'react-icons/io5';
import { useOrder } from '../../../hooks/useOrder';
import { PAYMENT_LIST } from '../../../config/payments';
import TransactionInfo from './components/transactionInfo/transactionInfo';

export default function Order() {
    const params = useParams();
    const orderId = Number(params.orderId);

    const { data: currentOrder, isLoading, isSuccess } = useGetOrderQuery({ id: orderId });
    const { order: updatedOrder } = useOrder();
    const order = updatedOrder ?? currentOrder;
    const product = order?.product;

    const { onCopy, value: valueCopy, setValue: setValueCopy, hasCopied } = useClipboard('');
    const toast = useToast();
    const [t] = useTranslation();

    const orderStatuses = Object.values(ORDER_STATUSES);
    const orderStatus = orderStatuses.find((status) => status.id === order?.orderStatus.id);

    const createdAt = new Date(order?.createdAt ?? '');
    const completedAt = new Date(order?.completedAt ?? '');
    const currentType = order?.gameActivationType === 'key' ? 'Steam Key' : 'Steam Gift';
    const isReplenishment = order?.type === 'replenishment';

    const paymentSystem = PAYMENT_LIST.find(
        (item) => item.name === order?.transaction?.paymentSystem,
    ) ?? '–';

    const value = order?.value ?? '';

    useEffect(() => {
        if (hasCopied) {
            toast({
                description: t('successfully-copied'),
                status: 'info',
                duration: 10000,
                render: () => (
                    <Flex
                        m={3}
                        color={Colors.lightBlue}
                        p={3}
                        bg={Colors.blue}
                        borderRadius="12px"
                        gap="2"
                        alignItems="center">
                        <CheckIcon />
                        <Box>{t('successfully-copied')}</Box>
                    </Flex>
                ),
            });
        }
    }, [hasCopied]);

    useEffect(() => {
        if (isSuccess && value) {
            setValueCopy(value);
        }
    }, [isSuccess]);

    return (
        <Container height="calc(100vh + 70px)" display="flex" flexDirection="column" gap="3">
            <Box
                backgroundImage={
                    process.env.PUBLIC_URL + '/images/categories/background_spotlight.jpg'
                }
                backgroundSize={'cover'}
                borderRadius="10px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                padding="26px 0px">
                <Text
                    color={Colors.lightBlue}
                    fontWeight="300"
                    wordBreak="break-all"
                    maxW="400px"
                    textAlign="center">
                    {isReplenishment ? t('you-top-up-with') : t('you-are-buying')}
                    <br /> {product?.name}
                </Text>
                <Text>
                    <span style={{ fontSize: '46px', color: '#fff' }}>
                        {order?.totalPrice.toLocaleString()}
                    </span>
                    <span
                        style={{
                            verticalAlign: 'baseline',
                            fontSize: '25px',
                            marginLeft: '5px',
                            color: Colors.lightGrey2,
                        }}>
                        {order?.localeType.currency}
                    </span>
                </Text>
                {orderStatus?.id === ORDER_STATUSES.completed.id && (
                    <Box
                        background={Colors.grey}
                        borderRadius="10px"
                        fontWeight="300"
                        minWidth="210px"
                        padding="5px 15px"
                        display="flex"
                        gap="2"
                        alignItems="center"
                        justifyContent="center"
                        color={hasCopied ? Colors.blue : 'inherit'}
                        onClick={onCopy}>
                        <Text>{truncate(value, 20)}</Text>
                        <CopyIcon />
                    </Box>
                )}
                {orderStatus?.id === ORDER_STATUSES.awaitingIssue.id && (
                    <Box
                        background={Colors.grey}
                        borderRadius="10px"
                        fontWeight="300"
                        minWidth="210px"
                        padding="10px 20px"
                        display="flex"
                        justifyContent="center"
                        gap="2"
                        alignItems="center">
                        <Text>{t('awaiting-issue')}</Text>
                        <Loader width="20px" height="20px" thickness="3px" />
                    </Box>
                )}
            </Box>
            <Box display="flex" flexDirection="column" gap="3">
                <Box display="flex" alignItems="center" gap="3">
                    <Box color="#fff">{orderStatus?.createIcon && orderStatus.createIcon()}</Box>
                    <Box fontWeight="300">
                        <Text color={Colors.darkGrey}>{t('status')}</Text>
                        <Text color="#fff">{t(orderStatus?.localeName)}</Text>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" gap="3">
                    <Box color="#fff">
                        <IoGameControllerOutline size="20px" />
                    </Box>
                    <Box fontWeight="300">
                        <Text color={Colors.darkGrey}>{t('type')}</Text>
                        <Text color="#fff">{isReplenishment ? t('top-up') : currentType}</Text>
                    </Box>
                </Box>
                <Divider borderColor="#485765" />

                {order && <TransactionInfo order={order} orderStatus={orderStatus} />}
            </Box>

            {!isReplenishment && (
                <Button
                    variant="none"
                    display="flex"
                    justifyContent="space-between"
                    padding="0"
                    color={Colors.lightBlue}
                    mt="20px"
                    width="fit-content"
                    gap="2"
                    as={Link}
                    to={'/product/' + product?.id + '?type=gift'}
                    state={{ pageName: 'Steam Gift' }}>
                    <Text fontWeight="300">{t('go-to-product-page')}</Text>
                    <ArrowForwardIcon boxSize={4} />
                </Button>
            )}
            {orderStatus?.id === ORDER_STATUSES.awaitingIssue.id && (
                <Text fontSize="12px" color={Colors.lightGrey2} fontWeight="300">
                    {t('awaiting-description')}
                </Text>
            )}
        </Container>
    );
}
